import { useEffect, useMemo, useState } from 'react';
import Carregando from '@/components/Carregando';
import { useLeads } from '@/leads/LeadsProvider';
import {
  porPaciente,
  rotulosDasFormas,
  totaisDaLista,
} from '@/leads/consolidacao';
import { chaveMes, mesesRecentes } from '@/metas/calculo';
import { buscarRecebimentosDoMes } from '@/services/remuneracaoService';

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const moedaCheia = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Recebimentos do mês (WG) — o que os pacientes PAGARAM no mês, um por linha.
 *
 * ★★ ESTA TELA MUDOU DE PERGUNTA em 28/set/2026.
 *
 * Até a WG ela listava "todo lead fechado que ainda tem saldo" — e não lia a
 * coleção `recebimentos`. O nome prometia mês e a tela entregava saldo. Medido
 * na época: 8 contratos em aberto contra **64 pacientes** com recebimento em
 * setembro. Agora ela é o que o nome diz.
 *
 * ★ A resposta antiga NÃO se perdeu: "quem ainda me deve" mudou de endereço para
 * `/recebimentos/em-aberto` (decisão do dono, saída B do plano). Ela tinha de
 * mudar de casa, e não virar coluna: um devedor que não pagou nada no mês **não
 * tem linha aqui** para carregar coluna nenhuma. Medido: recortando agosto ou
 * julho, os 8 devedores desapareceriam por completo — e em 1º de outubro, com o
 * mês virado, esta tela mostra zero linhas enquanto R$ 22.593,34 seguem a
 * receber.
 *
 * ★ O LANÇAMENTO MANUAL SAIU (decisão do dono): recebimento entra só pela
 * planilha do sistema da clínica. Diferente da WD-c2, que tirou o botão da ficha
 * e manteve a capacidade porque ESTA tela ainda lançava, agora **não sobra
 * nenhum caminho de tela** para criar recebimento. O serviço
 * (`registrarRecebimento`) e as rules continuam permitindo ao gestor — a
 * capacidade saiu da TELA, não do servidor. Ver `docs/wg-plano.md` §2.
 *
 * A fonte é `buscarRecebimentosDoMes` (`remuneracaoService.js:83`), a mesma
 * consulta que a Remuneração já usava — nenhum índice novo. Só gestor chega aqui
 * (rota e menu).
 */
export default function RecebimentosMesPage() {
  const { clinicaId, leads, carregando } = useLeads();
  const [mes, setMes] = useState(() => chaveMes(new Date()));
  const [recebimentos, setRecebimentos] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!clinicaId) return;
    setErro(null);
    setRecebimentos(null);
    buscarRecebimentosDoMes(clinicaId, mes)
      .then(setRecebimentos)
      .catch((e) => {
        console.error('[OdontoSDR] Falha ao carregar os recebimentos do mês:', e);
        setErro('Não foi possível carregar os recebimentos do mês.');
      });
  }, [clinicaId, mes]);

  // ★ Quem recorta o mês é `porPaciente` → `doMes`, comparando o TEXTO do campo
  // `mes`. A consulta acima abre ±1 dia de propósito por causa de fuso
  // (`remuneracaoService.js:70-77`); refazer o recorte por data aqui reintroduziria
  // exatamente o problema que aquela folga existe para evitar.
  const linhas = useMemo(
    () => porPaciente(recebimentos ?? [], leads ?? [], mes),
    [recebimentos, leads, mes],
  );
  const totais = useMemo(() => totaisDaLista(linhas), [linhas]);

  if (carregando || recebimentos === null) return <Carregando texto="Carregando os recebimentos…" />;

  return (
    <div className="pagina">
      <header className="pagina-cabecalho com-acao">
        <div>
          <h1>Recebimentos do mês</h1>
          <p className="painel-sub">
            {totais.pacientes} paciente{totais.pacientes === 1 ? '' : 's'} ·{' '}
            {totais.parcelas} parcela{totais.parcelas === 1 ? '' : 's'} ·{' '}
            <strong>{moedaCheia(totais.total)}</strong> recebidos em {mes}
          </p>
        </div>
        <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mês">
          {mesesRecentes().map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </header>

      {erro && <p className="erro">{erro}</p>}

      {linhas.length === 0 ? (
        <p className="vazio">
          Nenhum recebimento em {mes}. Os recebimentos entram pela importação da planilha do
          sistema da clínica.
        </p>
      ) : (
        <div className="tabela-envolve">
          <table className="tabela tabela-recebimentos">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Código Paciente</th>
                <th>Total pago no mês</th>
                <th>Parcelas</th>
                <th>Forma(s)</th>
                <th>SDR</th>
                {/* ★ WD-c2 (item 4) e WG: o mesmo rótulo da ficha — a mesma conta
                    não pode ter dois nomes. Aqui ele é informação da linha; a
                    LISTA de quem deve mora em /recebimentos/em-aberto. */}
                <th>Valor a pagar</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.leadId}>
                  <td><strong>{l.nome}</strong></td>
                  <td>{l.codigo || '—'}</td>
                  <td className="valor">{moedaCheia(l.total)}</td>
                  <td>{l.parcelas}</td>
                  <td>{rotulosDasFormas(l.formas)}</td>
                  <td>{l.sdrNome || '—'}</td>
                  <td className={l.saldo > 0 ? 'saldo-aberto' : undefined}>
                    {l.saldo > 0 ? moedaCheia(l.saldo) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total</th>
                <th>{moedaCheia(totais.total)}</th>
                <th>{totais.parcelas}</th>
                <th colSpan={2} />
                <th>{moeda(totais.saldo)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
